import { useState, useEffect, useCallback } from 'react'
import {
	Box,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	IconButton,
	useMediaQuery,
	useTheme
} from '@mui/material'
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material'
import { supabase } from "../utils/supabase"
import AddEditEmployeePage from './AddEditEmployeePage'
import { sfTint, sfInitials, statusViz } from '../utils/employeeDisplay'

// Nami lavender theme tokens (from the "Staff Management" design handoff)
const FONT = "'Outfit', sans-serif"
const EMP = {
	page: '#FBFAFE',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	danger: '#BE4B45',
	dangerSoft: '#FBECEB',
	headBg: '#EFEDF6',
	headText: '#6E6688'
}

function EmployeesManager({ merchant }) {
	const [employees, setEmployees] = useState([])
	const [services, setServices] = useState([])
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')
	const [saveError, setSaveError] = useState('')
	const [editorOpen, setEditorOpen] = useState(false)
	const [editingEmployee, setEditingEmployee] = useState(null)
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
	const [employeeToDelete, setEmployeeToDelete] = useState(null)
	const [query, setQuery] = useState('')

	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const loadEmployees = useCallback(async () => {
		setLoading(true)
		try {
			const { data, error } = await supabase
				.from('employees')
				.select('*')
				.eq('merchant_id', merchant.id)
				.order('first_name')

			if (error) throw error

			setEmployees(data || [])
		} catch (e) {
			console.error('Error loading employees:', e)
			setError('Failed to load employees')
		} finally {
			setLoading(false)
		}
	}, [merchant?.id])

	const loadServices = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('service_types')
				.select('id, name')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (error) throw error

			setServices(data || [])
		} catch (e) {
			console.error('Error loading services:', e)
		}
	}, [merchant?.id])

	useEffect(() => {
		if (merchant?.id) {
			loadEmployees()
			loadServices()
		}
	}, [merchant, loadEmployees, loadServices])

	const handleAddEmployee = () => {
		setEditingEmployee(null)
		setSaveError('')
		setEditorOpen(true)
	}

	const handleEditEmployee = (employee) => {
		setEditingEmployee(employee)
		setSaveError('')
		setEditorOpen(true)
	}

	const handleCloseEditor = () => {
		setEditorOpen(false)
		setEditingEmployee(null)
		setSaveError('')
	}

	const handleDeleteEmployee = (employee) => {
		setEmployeeToDelete(employee)
		setDeleteConfirmOpen(true)
	}

	const confirmDelete = async () => {
		if (!employeeToDelete) return

		try {
			const { error } = await supabase
				.from('employees')
				.delete()
				.eq('id', employeeToDelete.id)

			if (error) throw error

			setEmployees(prev => prev.filter(e => e.id !== employeeToDelete.id))
			setDeleteConfirmOpen(false)
			// If we were editing the deleted employee, return to the list
			if (editingEmployee && editingEmployee.id === employeeToDelete.id) {
				handleCloseEditor()
			}
			setEmployeeToDelete(null)
		} catch (e) {
			console.error('Error deleting employee:', e)
			setError('Failed to delete employee')
		}
	}

	const handleSubmit = async (form) => {
		setSaving(true)
		setSaveError('')

		try {
			const employeeData = {
				merchant_id: merchant.id,
				first_name: form.first_name,
				last_name: form.last_name,
				email: form.email || null,
				phone: form.phone || null,
				address: form.address || null,
				services: form.services,
				active: form.active
			}

			if (editingEmployee) {
				const { error } = await supabase
					.from('employees')
					.update(employeeData)
					.eq('id', editingEmployee.id)

				if (error) throw error

				setEmployees(prev => prev.map(e =>
					e.id === editingEmployee.id ? { ...e, ...employeeData } : e
				))
			} else {
				const { data, error } = await supabase
					.from('employees')
					.insert(employeeData)
					.select()
					.single()

				if (error) throw error

				setEmployees(prev => [...prev, data].sort((a, b) => a.first_name.localeCompare(b.first_name)))
			}

			handleCloseEditor()
		} catch (e) {
			console.error('Error saving employee:', e)
			setSaveError(e.message || 'Failed to save employee')
		} finally {
			setSaving(false)
		}
	}

	// Compact services label (empty = bookable for all services, preserving existing semantics)
	const servicesLabel = (employeeServices) => {
		const n = (employeeServices || []).length
		if (n === 0 || (services.length > 0 && n === services.length)) return 'All services'
		return `${n} ${n === 1 ? 'service' : 'services'}`
	}

	const fullName = (e) => `${e.first_name || ''} ${e.last_name || ''}`.trim()

	const q = query.trim().toLowerCase()
	const displayedEmployees = employees.filter((e) => {
		if (!q) return true
		return fullName(e).toLowerCase().includes(q) ||
			(e.email || '').toLowerCase().includes(q) ||
			(e.phone || '').toLowerCase().includes(q)
	})

	// Full-screen editor replaces the list (desktop inline / mobile fixed overlay)
	if (editorOpen) {
		return (
			<Box sx={{ height: '100%', overflow: 'auto', bgcolor: isMobile ? EMP.field : EMP.page, boxSizing: 'border-box' }}>
				<AddEditEmployeePage
					employee={editingEmployee}
					services={services}
					onSubmit={handleSubmit}
					onDelete={editingEmployee ? handleDeleteEmployee : null}
					onClose={handleCloseEditor}
					loading={saving}
					error={saveError}
					isMobile={isMobile}
				/>

				{/* Delete Confirmation Dialog */}
				<Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
					<DialogTitle>Remove staff member</DialogTitle>
					<DialogContent>
						<Typography>
							Are you sure you want to remove "{fullName(employeeToDelete || {})}"? This action cannot be undone.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
						<Button onClick={confirmDelete} color="error" variant="contained">Remove</Button>
					</DialogActions>
				</Dialog>
			</Box>
		)
	}

	return (
		<Box sx={{
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			padding: isMobile ? '0' : '22px 26px 30px',
			bgcolor: isMobile ? EMP.field : EMP.page,
			boxSizing: 'border-box'
		}}>
			{/* ── Header ── */}
			{isMobile ? (
				<Box sx={{ flex: 'none', padding: '16px 16px 12px', bgcolor: EMP.field, borderBottom: `1px solid ${EMP.border}` }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
						<Typography sx={{ fontFamily: FONT, fontSize: '30px', fontWeight: 800, letterSpacing: '-0.02em', color: EMP.text }}>
							Staff
						</Typography>
						<Button
							onClick={handleAddEmployee}
							startIcon={<AddIcon sx={{ fontSize: 16 }} />}
							sx={{ height: '40px', px: '15px', border: 'none', borderRadius: '11px', bgcolor: EMP.primary, color: '#FFFFFF', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '14px', boxShadow: '0 2px 8px rgba(124,107,217,0.28)', '&:hover': { bgcolor: EMP.primaryDeep }, '& .MuiButton-startIcon': { marginRight: '6px' } }}
						>
							New
						</Button>
					</Box>
					<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: EMP.muted, mt: '2px' }}>
						{employees.length} team {employees.length === 1 ? 'member' : 'members'}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '12px', height: '40px', padding: '0 12px', borderRadius: '11px', bgcolor: EMP.surface, color: EMP.muted }}>
						<SearchIcon sx={{ fontSize: 16 }} />
						<Box
							component="input"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search staff"
							sx={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT, fontSize: '15px', color: EMP.text, width: '100%', '&::placeholder': { color: EMP.muted } }}
						/>
					</Box>
				</Box>
			) : (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', mb: '18px' }}>
					<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '11px' }}>
						<Typography sx={{ fontFamily: FONT, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em', color: EMP.text }}>
							Staff
						</Typography>
						<Typography sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 650, color: '#9A93B4' }}>
							{employees.length} team {employees.length === 1 ? 'member' : 'members'}
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px', width: '248px', padding: '0 13px', borderRadius: '10px', bgcolor: EMP.field, border: `1.5px solid ${EMP.border}`, color: EMP.muted, '&:focus-within': { borderColor: EMP.primary } }}>
							<SearchIcon sx={{ fontSize: 15 }} />
							<Box
								component="input"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search staff"
								sx={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT, fontSize: '13.5px', color: EMP.text, width: '100%', '&::placeholder': { color: EMP.muted } }}
							/>
						</Box>
						<Button
							onClick={handleAddEmployee}
							startIcon={<AddIcon sx={{ fontSize: 15 }} />}
							sx={{ height: '40px', px: '16px', border: 'none', borderRadius: '10px', bgcolor: EMP.primary, color: '#FFFFFF', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '13.5px', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(124,107,217,0.28)', '&:hover': { bgcolor: EMP.primaryDeep }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
						>
							New staff member
						</Button>
					</Box>
				</Box>
			)}

			{error && (
				<Alert severity="error" sx={{ m: isMobile ? '12px 16px 0' : '0 0 16px' }}>
					{error}
				</Alert>
			)}

			{/* ── List ── */}
			<Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', padding: isMobile ? '12px 16px 20px' : 0 }}>
				{loading ? (
					<Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: EMP.muted, p: isMobile ? 0 : '4px 2px' }}>
						Loading staff...
					</Typography>
				) : displayedEmployees.length === 0 ? (
					<Box sx={{ bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '16px', padding: '30px 16px', textAlign: 'center', color: EMP.muted, fontFamily: FONT, fontWeight: 600, fontSize: '14px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)' }}>
						{q ? `No staff match “${query}”.` : 'No staff yet. Add your first team member to get started.'}
					</Box>
				) : isMobile ? (
					/* ── Mobile card list ── */
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
						{displayedEmployees.map((e) => {
							const tint = sfTint(fullName(e))
							const sv = statusViz(e.active)
							return (
								<Box
									key={e.id}
									onClick={() => handleEditEmployee(e)}
									sx={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '13px 14px', bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '16px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)', cursor: 'pointer', '&:active': { bgcolor: '#FBFAFE' } }}
								>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
										<Box sx={{ width: 46, height: 46, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontSize: '15px', fontWeight: 800, bgcolor: tint.bg, color: tint.fg }}>
											{sfInitials(fullName(e))}
										</Box>
										<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
											<Typography sx={{ fontFamily: FONT, fontSize: '15.5px', fontWeight: 750, letterSpacing: '-0.01em', color: EMP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
												{fullName(e) || 'Unnamed'}
											</Typography>
											<Typography sx={{ fontFamily: FONT, fontSize: '12.5px', fontWeight: 600, color: EMP.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
												{servicesLabel(e.services)}
											</Typography>
										</Box>
										<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '24px', padding: '0 10px 0 8px', flex: 'none', borderRadius: '99px', fontFamily: FONT, fontSize: '11.5px', fontWeight: 700, bgcolor: sv.bg, color: sv.fg }}>
											<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sv.dot }} />
											{sv.label}
										</Box>
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
										<Typography sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: EMP.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
											{e.email || 'No email'}
										</Typography>
										<Typography sx={{ flex: 'none', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: EMP.muted, whiteSpace: 'nowrap' }}>
											{e.phone || 'No phone'}
										</Typography>
									</Box>
								</Box>
							)
						})}
					</Box>
				) : (
					/* ── Desktop table ── */
					<Box sx={{ bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(46,42,64,0.04)' }}>
						<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
							<Box component="thead">
								<Box component="tr">
									{['Name', 'Email', 'Phone', 'Services', 'Status'].map((label) => (
										<Box component="th" key={label} sx={{ textAlign: 'left', fontFamily: FONT, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: EMP.headText, p: '13px 18px', bgcolor: EMP.headBg, borderBottom: `1.5px solid ${EMP.border}`, whiteSpace: 'nowrap' }}>
											{label}
										</Box>
									))}
									<Box component="th" sx={{ textAlign: 'right', width: '96px', fontFamily: FONT, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: EMP.headText, p: '13px 18px', bgcolor: EMP.headBg, borderBottom: `1.5px solid ${EMP.border}`, whiteSpace: 'nowrap' }}>
										Actions
									</Box>
								</Box>
							</Box>
							<Box component="tbody">
								{displayedEmployees.map((e, index) => {
									const tint = sfTint(fullName(e))
									const sv = statusViz(e.active)
									const isLast = index === displayedEmployees.length - 1
									const td = { p: '12px 18px', borderBottom: isLast ? 'none' : `1px solid ${EMP.surface}`, verticalAlign: 'middle' }
									return (
										<Box
											component="tr"
											key={e.id}
											onClick={() => handleEditEmployee(e)}
											sx={{ cursor: 'pointer', transition: 'background 0.12s', '&:hover': { bgcolor: EMP.primarySoft } }}
										>
											<Box component="td" sx={td}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
													<Box sx={{ width: 38, height: 38, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontSize: '13px', fontWeight: 800, bgcolor: tint.bg, color: tint.fg }}>
														{sfInitials(fullName(e))}
													</Box>
													<Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: EMP.text, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
														{fullName(e) || 'Unnamed'}
													</Typography>
												</Box>
											</Box>
											<Box component="td" sx={td}>
												<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 500, color: e.email ? EMP.muted : '#B4ADC6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>
													{e.email || '—'}
												</Typography>
											</Box>
											<Box component="td" sx={td}>
												<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 500, color: e.phone ? EMP.muted : '#B4ADC6', whiteSpace: 'nowrap' }}>
													{e.phone || '—'}
												</Typography>
											</Box>
											<Box component="td" sx={td}>
												<Typography sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 600, color: EMP.text, whiteSpace: 'nowrap' }}>
													{servicesLabel(e.services)}
												</Typography>
											</Box>
											<Box component="td" sx={td}>
												<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '24px', padding: '0 10px 0 8px', borderRadius: '99px', fontFamily: FONT, fontSize: '12px', fontWeight: 700, bgcolor: sv.bg, color: sv.fg }}>
													<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sv.dot }} />
													{sv.label}
												</Box>
											</Box>
											<Box component="td" sx={{ ...td, width: '96px' }} onClick={(ev) => ev.stopPropagation()}>
												<Box sx={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
													<IconButton
														onClick={() => handleEditEmployee(e)}
														aria-label="Edit"
														sx={{ width: 30, height: 30, border: `1px solid ${EMP.border}`, borderRadius: '8px', bgcolor: EMP.field, color: EMP.muted, '&:hover': { color: EMP.primaryDeep, borderColor: EMP.primary, bgcolor: EMP.field } }}
													>
														<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 2.5l2 2L6 12l-2.6.6L4 10z" /></svg>
													</IconButton>
													<IconButton
														onClick={() => handleDeleteEmployee(e)}
														aria-label="Delete"
														sx={{ width: 30, height: 30, border: `1px solid ${EMP.border}`, borderRadius: '8px', bgcolor: EMP.field, color: EMP.muted, '&:hover': { color: EMP.danger, borderColor: EMP.danger, bgcolor: EMP.dangerSoft } }}
													>
														<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M3 4.5h10M6.5 4V3h3v1M5 4.5l.5 8h5l.5-8" /></svg>
													</IconButton>
												</Box>
											</Box>
										</Box>
									)
								})}
							</Box>
						</Box>
					</Box>
				)}
			</Box>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Remove staff member</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to remove "{fullName(employeeToDelete || {})}"? This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button onClick={confirmDelete} color="error" variant="contained">Remove</Button>
				</DialogActions>
			</Dialog>
		</Box>
	)
}

export default EmployeesManager
